import { Card } from '@components/card';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { styledTranslate, useScopedTranslate } from '@locale/translate';

import { IssueAction } from 'api/journeys/requests';
import { ActionIcon, ActionLabel } from 'ui/actions/action-config';
import { showActionSidebar } from 'ui/journey-builder/show-action-sidebar';

export const useIssuesSidebar = () =>
  useSidebar(IssuesSidebar, {
    variant: 'REGULAR',
    hasBackdrop: false,
  });

const IssuesSidebar = ({
  issues,
  numAttributes,
}: {
  issues: IssueAction[];
  numAttributes: number;
}) => {
  const { t } = useScopedTranslate(locale);

  const hasIssues = issues.length > 0;

  const hasJourneyAttributes = numAttributes > 0;
  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        title={t('issues', { num_issues: issues.length })}
        icon={hasIssues ? IconFormIndicatorWarning : IconFormIndicatorSuccess}
      />
      <Sidebar.Content>
        <Stack space={6}>
          {hasIssues ? (
            <>
              <Text
                text={styledTranslate({
                  key: issues.length === 1 ? 'issuesDescriptionOne' : 'issuesDescriptionMany',
                  variables: {
                    issues: {
                      text:
                        issues.length === 1
                          ? t('issueOne')
                          : t('issueMany', { num_issues: issues.length }),
                      color: 'elementBlue100',
                      font: 'regular14',
                    },
                  },
                  translations: locale,
                })}
                font="regular14"
              />
              <Stack space={4}>
                {issues.map((issue, idx) => (
                  <IssueCard issue={issue} key={idx} />
                ))}
              </Stack>
            </>
          ) : (
            <Stack space={4}>
              <Text text={t('noIssues')} font="regular14" />
              {hasJourneyAttributes && (
                <div>
                  <Text text={t('pleaseNote')} font="semibold14" />
                  <Text
                    text={styledTranslate({
                      key:
                        numAttributes === 1
                          ? 'noteOnJourneyAttributesOne'
                          : 'noteOnJourneyAttributesMany',
                      variables: {
                        attributes: {
                          text:
                            numAttributes === 1
                              ? t('numJourneyAttributesOne')
                              : t('numJourneyAttributesMany', { num_attributes: numAttributes }),
                          color: 'elementBlue100',
                          font: 'regular14',
                        },
                      },
                      translations: locale,
                    })}
                    font="regular14"
                  />
                </div>
              )}
            </Stack>
          )}
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

const IssueCard = ({ issue }: { issue: IssueAction }) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Stack space={2}>
      <Card
        spaceY={2}
        spaceX={4}
        interactive
        onClick={() =>
          showActionSidebar({ id: issue.shortId, type: issue.action }, { focusNode: true })
        }
        style={{ cursor: 'pointer' }}
      >
        <Stack space={4} orientation="horizontal" align="center" justify="space-between">
          <Stack space={2} orientation="horizontal" align="center">
            <ActionIcon action={issue.action} />
            <div>
              <ActionLabel action={issue.action} font="semibold14" />
              <Text
                text={issue.description ?? t('noDescription')}
                font="regular14"
                color="textSecondary"
              />
            </div>
          </Stack>
          <IconFormIndicatorWarning size="24" />
        </Stack>
      </Card>
    </Stack>
  );
};

const locale = {
  en: {
    issues: 'Issues • {num_issues}',
    issueOne: '1 issue',
    issueMany: '{num_issues} issues',
    issuesDescriptionOne:
      'There is {issues} with this journey that needs to be resolved before publishing',
    issuesDescriptionMany:
      'There are {issues} with this journey that need to be resolved before publishing',
    noIssues: 'There are no issues with this journey and it should run normally',
    pleaseNote: 'Please note:',
    numJourneyAttributesOne: '1 journey field',
    numJourneyAttributesMany: '{num_attributes} journey fields',
    noteOnJourneyAttributesOne:
      'There is {attributes} in this journey\n\nWhen adding Travellers to this journey make sure to configure the journey field for each Traveller',
    noteOnJourneyAttributesMany:
      'There are {attributes} in this journey\n\nWhen adding Travellers to this journey make sure to configure the journey fields for each Traveller',
    noDescription: 'No description',
  },
};
